import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Utiliser useRef pour l'instance API pour qu'elle ne change jamais
  const APIRef = useRef(null);

  if (!APIRef.current) {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Intercepteur pour ajouter le token
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth-token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    APIRef.current = instance;
  }

  // Fonction pour récupérer les données utilisateur - utilise useRef pour éviter la dépendance
  const getUserData = React.useCallback(async () => {
    const token = localStorage.getItem('auth-token');
    if (!token) {
      setUserData(null);
      setLoading(false);
      return;
    }

    try {
      const response = await APIRef.current.get('/api/user/data');
      if (response.data.success) {
        const baseUserData = response.data.userData;

        // Générer l'URL signée pour la photo si elle existe
        if (baseUserData.profilePhoto) {
          try {
            const photoResponse = await APIRef.current.get('/api/user/photo');
            if (photoResponse.data.success) {
              setUserData({
                ...baseUserData,
                profilePhotoUrl: photoResponse.data.photoUrl
              });
            } else {
              setUserData(baseUserData);
            }
          } catch (photoError) {
            console.error('Error fetching photo URL:', photoError);
            setUserData(baseUserData);
          }
        } else {
          setUserData(baseUserData);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      if (error.response?.status === 401) {
        // Token invalide, déconnecter
        localStorage.removeItem('auth-token');
        setUser(null);
        setUserData(null);
      }
    } finally {
      setLoading(false);
    }
  }, []); // Pas de dépendances - stable pour toujours

  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      setUser({ token });
      getUserData();
    } else {
      setUser(null);
      setUserData(null);
      setLoading(false);
    }
  }, []); // S'exécute une seule fois au montage

  return (
    <AuthContext.Provider value={{ user, setUser, userData, getUserData, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);